import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, distinctUntilChanged, filter, interval, map, take } from 'rxjs';
import { AuthService } from './core/auth/auth.service';
import { ToasterService } from './core/modules/toaster/toaster.service';
import { NotificationsService } from './core/services/notifications.service';

@Component({
    selector: 'em-root',
    templateUrl: './app.component.html',
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly toasterService = inject(ToasterService);
  private readonly notificationsService = inject(NotificationsService);
  private authSubscription: Subscription | null = null;
  private tokenExpirySubscription: Subscription | null = null;

  ngOnInit(): void {
    // Shell-level lifecycle: auth presence drives polling and token-expiry monitoring.
    this.authSubscription = this.authService.user$.pipe(
      map((user) => user != null),
      distinctUntilChanged(),
    ).subscribe((hasAuthenticatedUser) => {
      if (!hasAuthenticatedUser) {
        this.stopTokenExpiryMonitoring();
        this.notificationsService.stopPolling();
        this.notificationsService.activeRequestCount$.next(0);
        return;
      }

      this.startTokenExpiryMonitoring();
      this.notificationsService.refreshActiveRequestCount();
      this.notificationsService.startPolling();
    });
  }

  ngOnDestroy(): void {
    this.authSubscription?.unsubscribe();
    this.stopTokenExpiryMonitoring();
    this.notificationsService.stopPolling();
  }

  private startTokenExpiryMonitoring(): void {
    this.stopTokenExpiryMonitoring();

    this.tokenExpirySubscription = interval(30_000).pipe(
      filter(() => this.authService.token != null),
      filter(() => this.authService.tokenInvalidOrExpired(this.authService.token)),
      take(1),
    ).subscribe(() => {
      this.authService.resetSession();
      this.notificationsService.stopPolling();
      this.notificationsService.activeRequestCount$.next(0);
      void this.router.navigate(['/login']);
      this.toasterService.error('Session Expired. Please login to continue.');
    });
  }

  private stopTokenExpiryMonitoring(): void {
    this.tokenExpirySubscription?.unsubscribe();
    this.tokenExpirySubscription = null;
  }
}
